import { component, prop, emit } from '../../../decorators';

import MaskedInput from '../masked-input/masked-input.vue';

import reduce from 'lodash/reduce';
import reduceRight from 'lodash/reduceRight';

import { START_EPISODE_AT } from '../../../config';
import l10n from '../../../transformers/l10n';

const SEC_IN_SEC  = 1;
const SEC_IN_MIN  = 60;
const MIN_IN_HOUR = 60;
const SEC_IN_HOUR = SEC_IN_MIN * MIN_IN_HOUR;
const MS_IN_SEC   = 1000;
const DELIM = ':';
const ZERO = 0;

const MULTIPLIERS = [ SEC_IN_HOUR, SEC_IN_MIN, SEC_IN_SEC ];

const _increment = i => ++i;
const _decrement = i => --i;

@component({
   components: { MaskedInput },
   watch: {
      value: 'normalize'
   }
})
export default class TimecodeInput {
   @prop({ }) value
   @prop({ type: Number, required: true }) frameRate
   @prop({ type: Number, required: true }) duration
   @prop({ type: Number, required: true }) timecode
   @prop({ type: Boolean, default: false }) readonly
   @prop({ type: Boolean, default: false }) disabled

   @emit('change') changeTime

   get frameDuration() {
      return MS_IN_SEC / this.frameRate;
   }

   get formattedValue() {
      let num = this.value / MS_IN_SEC;
      const res = reduce(MULTIPLIERS, (memo, multiplier) => {
         memo = memo ? `${memo}:` : '';
         memo += _pad(Math.floor(num / multiplier));
         num = num % multiplier;
         return memo;
      }, '');

      return `${res}.${this._numberOfFrameInCurrentSec(this.value)}`;
   }

   get prevTitle() {
      return l10n('timecode.prev');
   }

   get nextTitle() {
      return l10n('timecode.next');
   }

   _getTimeFromInput() {
      const strVal = this.$refs.input.getValue();
      let [ str, frames ] = strVal.split('_').join('').split('.');
      const times = str.split(DELIM);

      const time = reduceRight(MULTIPLIERS, (memo, multiplier) => {
         return memo + multiplier * (+times.pop() || ZERO);
      }, ZERO);

      // decrease frames for using 0 instead of 1
      return Math.ceil(time * MS_IN_SEC + --frames * this.frameDuration);
   }

   refreshValue() {
      this.changeTime(this._getTimeFromInput() + this.frameDuration);
   }

   prev() {
      this._moveByFrame(_decrement);
   }

   next() {
      this._moveByFrame(_increment);
   }

   _moveByFrame(operation) {
      const time = this._getTimeFromInput() - this.timecode;
      const frameDuration = this.frameDuration;
      let frameNumber = Math.ceil(time / frameDuration);
      const newTime = Math.ceil(operation(frameNumber) * frameDuration) + this.timecode;
      this.changeTime(newTime);
   }

   normalize() {
      let { value } = this;
      value = Math.min(value, this.duration + this.timecode);
      value = Math.max(value, START_EPISODE_AT);

      if ( !(value % MS_IN_SEC) ) {
         value = this._startFrameTime(value) + this.frameDuration;
         if (value !== this.value) {
            this.changeTime(value);
         }
      } else if (value !== this.value) {
         this.changeTime(value);
      }
   }

   _startFrameTime(time) {
      const played = time - this.timecode;
      return Math.floor(Math.floor(played / this.frameDuration) * this.frameDuration) + this.timecode;
   }

   _timeForFirstFrameInSec(time) {
      const sec = Math.floor((time - this.timecode) / MS_IN_SEC) * MS_IN_SEC;
      // get start frame time for 0ms. This will be last frame in prev sec, and add frame duration to detect first time
      return this._startFrameTime(sec + this.timecode) + this.frameDuration;
   }

   _numberOfFrameInCurrentSec(time) {
      const tfffis = this._timeForFirstFrameInSec(time);
      const timeFromFirstFrame = time - tfffis;
      let number = Math.floor(timeFromFirstFrame / this.frameDuration);
      return ++number || 1; // eslint-disable-line no-magic-numbers
   }

   created() {
      this.normalize();
   }
}


function _pad(num) {
   return num > 9 ? `${num}` : `0${num}`; // eslint-disable-line no-magic-numbers
}
